import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Loader2, Share2, Twitter, Facebook, Linkedin, Link2, Check, ExternalLink, Instagram, Mail, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Bluesky icon component
const BlueskyIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z"/>
  </svg>
);

const HonoreesPage = () => {
  const [honorees, setHonorees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedHonoree, setSelectedHonoree] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [copied, setCopied] = useState(false);
  const [newsletterForm, setNewsletterForm] = useState({ name: '', email: '' });
  const [newsletterStatus, setNewsletterStatus] = useState('idle');
  const [newsletterError, setNewsletterError] = useState('');

  useEffect(() => {
    const fetchHonorees = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}/api/honorees`);
        setHonorees(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching honorees:', err);
        setError('Failed to load honorees. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchHonorees();
  }, []);

  const filteredHonorees = honorees.filter(honoree =>
    honoree.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    honoree.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    honoree.title?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Separate featured and regular honorees (up to 3 featured)
  const featuredHonorees = filteredHonorees.filter(h => h.featured).slice(0, 3);
  const regularHonorees = filteredHonorees.filter(h => !h.featured);

  // Share functionality - now uses individual honoree URL
  const getShareUrl = (honoree) => {
    return `${window.location.origin}/honorees/${honoree.id}`;
  };

  const getShareText = (honoree) => {
    return `🖖 Congratulations to ${honoree.name} for receiving the LLAP Tribute Award from the Nimoy-Knight Foundation! #LLAP #RememberingLeonardNimoy`;
  };

  const shareToTwitter = (honoree) => {
    const text = getShareText(honoree);
    const url = getShareUrl(honoree);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
  };

  const shareToFacebook = (honoree) => {
    const url = getShareUrl(honoree);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(getShareText(honoree))}`, '_blank');
  };

  const shareToLinkedIn = (honoree) => {
    const url = getShareUrl(honoree);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
  };

  const copyLink = async (honoree) => {
    try {
      await navigator.clipboard.writeText(getShareUrl(honoree));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterStatus('loading');
    setNewsletterError('');

    try {
      await axios.post(`${API_URL}/api/newsletter-signups`, newsletterForm);
      setNewsletterStatus('success');
      setNewsletterForm({ name: '', email: '' });
    } catch (err) {
      setNewsletterStatus('error');
      setNewsletterError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">🖖</div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">LLAP Award </span>
            <span className="text-amber-500">Honorees</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Celebrating extraordinary individuals who embody Leonard Nimoy's message of hope, logic, compassion, and service to others.
          </p>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-8 bg-[#0a0a0b]">
        <div className="max-w-2xl mx-auto px-4">
          <div className="relative">
            <label htmlFor="honoree-search" className="sr-only">Search honorees</label>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" aria-hidden="true" />
            <input
              type="text"
              id="honoree-search"
              placeholder="Search honorees by name, title, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-[#141416] border border-gray-800 rounded-full text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
            />
          </div>
        </div>
      </section>

      {/* Featured Honorees Section */}
      {featuredHonorees.length > 0 && !searchTerm && (
        <section className="py-12 bg-gradient-to-b from-[#0f0f12] to-[#0a0a0b]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-500 text-sm font-semibold rounded-full border border-amber-500/30">
                ⭐ Featured Honoree{featuredHonorees.length > 1 ? 's' : ''}
              </span>
            </div>
            
            {/* Single Featured Honoree - Large Layout */}
            {featuredHonorees.length === 1 && (
              <div className="bg-[#141416] border border-amber-500/30 rounded-3xl overflow-hidden shadow-2xl shadow-amber-500/5">
                <div className="flex flex-col lg:flex-row">
                  <div className="lg:w-2/5 aspect-square lg:aspect-auto">
                    <img
                      src={featuredHonorees[0].image}
                      alt={`${featuredHonorees[0].name} - Featured LLAP Award Honoree`}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                  <div className="lg:w-3/5 p-8 lg:p-12 flex flex-col justify-center">
                    <div className="mb-4">
                      <span className="inline-block px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
                        LLAP HONOREE
                      </span>
                    </div>
                    <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                      {featuredHonorees[0].name}
                    </h2>
                    <p className="text-amber-500 text-lg mb-6">
                      {featuredHonorees[0].title}
                      {featuredHonorees[0].organization && ` • ${featuredHonorees[0].organization}`}
                    </p>
                    {featuredHonorees[0].quote && (
                      <div className="mb-8">
                        <div className="text-amber-500 text-4xl font-serif leading-none mb-3">"</div>
                        <div className="text-gray-300 leading-relaxed space-y-4 max-h-[400px] overflow-y-auto pr-4 custom-scrollbar">
                          {featuredHonorees[0].quote.split('\n\n').map((paragraph, idx) => (
                            <p key={idx} className="text-base">{paragraph}</p>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-4 items-center">
                      <Link
                        to={`/honorees/${featuredHonorees[0].id}`}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Full Profile
                      </Link>
                      <button
                        onClick={() => shareToTwitter(featuredHonorees[0])}
                        className="inline-flex items-center gap-2 px-4 py-3 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-full text-[#1DA1F2] font-medium transition-all"
                      >
                        <Twitter className="w-4 h-4" />
                        Share
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Multiple Featured Honorees - Grid Layout */}
            {featuredHonorees.length > 1 && (
              <div className={`grid gap-6 ${featuredHonorees.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`}>
                {featuredHonorees.map((honoree, index) => (
                  <div 
                    key={honoree.id} 
                    className="bg-[#141416] border border-amber-500/30 rounded-2xl overflow-hidden shadow-xl shadow-amber-500/5 hover:border-amber-500/50 transition-all group"
                  >
                    <div className="aspect-square relative overflow-hidden">
                      <img
                        src={honoree.image}
                        alt={`${honoree.name} - Featured LLAP Award Honoree`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                      <div className="absolute top-4 left-4">
                        <span className="inline-block px-3 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
                          FEATURED
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-1 group-hover:text-amber-500 transition-colors">
                        {honoree.name}
                      </h3>
                      <p className="text-amber-500 text-sm mb-4">
                        {honoree.title}
                        {honoree.organization && ` • ${honoree.organization}`}
                      </p>
                      {honoree.quote && (
                        <p className="text-gray-400 text-sm mb-4 line-clamp-3 italic">
                          "{honoree.quote.split('\n\n')[0].substring(0, 150)}..."
                        </p>
                      )}
                      <div className="flex gap-3">
                        <Link
                          to={`/honorees/${honoree.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black text-sm font-semibold rounded-full transition-all"
                        >
                          View Profile
                        </Link>
                        <button
                          onClick={() => shareToTwitter(honoree)}
                          className="inline-flex items-center justify-center p-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-full text-[#1DA1F2] transition-all"
                          title="Share on Twitter"
                        >
                          <Twitter className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* All Honorees Header */}
      {featuredHonorees.length > 0 && !searchTerm && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-6">
          <h2 className="text-2xl font-bold text-white">All Honorees</h2>
        </div>
      )}

      {/* Honorees Grid */}
      <section className={`${featuredHonorees.length > 0 && !searchTerm ? 'pt-0' : 'py-16'} pb-16 bg-[#0a0a0b]`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
              <span className="ml-3 text-gray-400">Loading honorees...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="text-center py-16">
              <p className="text-red-400 text-lg">{error}</p>
            </div>
          )}

          {/* Honorees Grid */}
          {!loading && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {(searchTerm ? filteredHonorees : regularHonorees).map((honoree) => (
              <div
                key={honoree.id}
                onClick={() => setSelectedHonoree(honoree)}
                className="bg-[#141416] border border-gray-800/50 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all cursor-pointer group"
              >
                {/* Image */}
                <div className="aspect-square overflow-hidden bg-gray-900">
                  <img
                    src={honoree.image}
                    alt={`${honoree.name} - LLAP Award Honoree`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                
                {/* Content */}
                <div className="p-5">
                  <span className="inline-block px-2 py-1 bg-amber-500/10 text-amber-500 text-xs font-semibold rounded-full mb-3">
                    LLAP Honoree
                  </span>
                  <h3 className="text-lg font-bold text-white mb-1 group-hover:text-amber-500 transition-colors">
                    {honoree.name}
                  </h3>
                  <p className="text-gray-500 text-sm">{honoree.title}</p>
                  {honoree.organization && (
                    <p className="text-amber-500/70 text-sm">{honoree.organization}</p>
                  )}
                  {honoree.quote && (
                    <p className="text-gray-400 text-sm mt-3 line-clamp-2">
                      "{honoree.quote.substring(0, 80)}..."
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          )}

          {filteredHonorees.length === 0 && !loading && !error && (
            <div className="text-center py-16">
              <p className="text-gray-400 text-lg">No honorees found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Modal */}
      <Dialog open={!!selectedHonoree} onOpenChange={() => setSelectedHonoree(null)}>
        <DialogContent className="bg-[#141416] border-gray-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={selectedHonoree?.image}
                  alt={`${selectedHonoree?.name} - LLAP Award Honoree`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div>
                <span className="inline-block px-2 py-1 bg-amber-500/10 text-amber-500 text-xs font-semibold rounded-full mb-1">
                  LLAP Honoree
                </span>
                <DialogTitle className="text-2xl font-bold">
                  {selectedHonoree?.name}
                </DialogTitle>
                <p className="text-gray-500">{selectedHonoree?.title}</p>
                {selectedHonoree?.organization && (
                  <p className="text-amber-500">{selectedHonoree?.organization}</p>
                )}
              </div>
            </div>
          </DialogHeader>
          <div className="mt-6">
            {selectedHonoree?.quote ? (
              <>
                <div className="text-amber-500 text-3xl font-serif mb-2">"</div>
                <p className="text-gray-300 leading-relaxed italic">
                  {selectedHonoree?.quote}
                </p>
              </>
            ) : (
              <p className="text-gray-400">This honoree has been recognized for their outstanding contributions to the community.</p>
            )}
          </div>
          
          {/* Share Section */}
          <div className="mt-8 pt-6 border-t border-gray-800">
            {/* View Profile Link */}
            <Link
              to={selectedHonoree ? `/honorees/${selectedHonoree.id}` : '#'}
              className="flex items-center justify-center gap-2 w-full py-3 mb-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all"
              data-testid="view-full-profile-btn"
            >
              <ExternalLink className="w-4 h-4" />
              View Full Profile
            </Link>
            
            <div className="flex items-center gap-2 mb-4">
              <Share2 className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-semibold text-white">Share this honoree</span>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => shareToTwitter(selectedHonoree)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/30 rounded-full text-[#1DA1F2] text-sm font-medium transition-all"
                aria-label="Share on Twitter"
                data-testid="share-twitter-btn"
              >
                <Twitter className="w-4 h-4" />
                Twitter
              </button>
              <button
                onClick={() => shareToFacebook(selectedHonoree)}
                className="flex items-center gap-2 px-4 py-2 bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border border-[#1877F2]/30 rounded-full text-[#1877F2] text-sm font-medium transition-all"
                aria-label="Share on Facebook"
                data-testid="share-facebook-btn"
              >
                <Facebook className="w-4 h-4" />
                Facebook
              </button>
              <button
                onClick={() => shareToLinkedIn(selectedHonoree)}
                className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 border border-[#0A66C2]/30 rounded-full text-[#0A66C2] text-sm font-medium transition-all"
                aria-label="Share on LinkedIn"
                data-testid="share-linkedin-btn"
              >
                <Linkedin className="w-4 h-4" />
                LinkedIn
              </button>
              <button
                onClick={() => copyLink(selectedHonoree)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-full text-sm font-medium transition-all ${
                  copied 
                    ? 'bg-green-500/10 border-green-500/30 text-green-500' 
                    : 'bg-gray-800/50 hover:bg-gray-800 border-gray-700 text-gray-300'
                }`}
                aria-label="Copy link"
                data-testid="share-copy-link-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Copy Link
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* IDIC Section */}
      <section className="py-24 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-6">🖖</div>
          <h2 className="text-3xl font-bold text-white mb-6">
            IDIC: Infinite Diversity in Infinite Combinations
          </h2>
          <p className="text-gray-400 leading-relaxed">
            A Vulcan philosophy that celebrates the vast variety of the universe. This principle, central to Spock's character, reminds us that diversity is not just to be tolerated — it is to be celebrated as the source of all beauty and strength.
          </p>
        </div>
      </section>

      {/* Newsletter Signup Section */}
      <section className="py-20 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#141416] border border-amber-500/20 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-full mb-4">
                <Mail className="w-8 h-8 text-amber-500" />
              </div>
              <h2 className="text-3xl font-bold text-white mb-4">
                Stay Connected
              </h2>
              <p className="text-gray-300 text-lg max-w-xl mx-auto">
                Become the first to get the latest news and updates from The Nimoy Knight Foundation by signing up for our email newsletter!
              </p>
            </div>

            {newsletterStatus === 'success' ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">You're Subscribed!</h3>
                <p className="text-gray-300">
                  Thank you for signing up. You'll receive our latest news and updates.
                </p>
                <button
                  onClick={() => setNewsletterStatus('idle')}
                  className="mt-6 text-amber-500 hover:text-amber-400 underline"
                >
                  Sign up another person
                </button>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto space-y-4">
                <div>
                  <label htmlFor="honorees-newsletter-name" className="block text-gray-400 text-sm mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="honorees-newsletter-name"
                    value={newsletterForm.name}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, name: e.target.value })}
                    required
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                    data-testid="honorees-newsletter-name-input"
                  />
                </div>
                
                <div>
                  <label htmlFor="honorees-newsletter-email" className="block text-gray-400 text-sm mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="honorees-newsletter-email"
                    value={newsletterForm.email}
                    onChange={(e) => setNewsletterForm({ ...newsletterForm, email: e.target.value })}
                    required
                    placeholder="Enter your email"
                    className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                    data-testid="honorees-newsletter-email-input"
                  />
                </div>

                {newsletterStatus === 'error' && (
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                    {newsletterError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={newsletterStatus === 'loading'}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black font-bold rounded-xl transition-all hover:scale-[1.02] disabled:hover:scale-100"
                  data-testid="honorees-newsletter-submit-btn"
                >
                  {newsletterStatus === 'loading' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Subscribing...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Subscribe to Newsletter
                    </>
                  )}
                </button>

                <p className="text-center text-gray-500 text-xs mt-4">
                  🖖 Live Long and Prosper
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HonoreesPage;
